import {
    Add,
    FilterAltOffOutlined,
    FilterAltOutlined,
} from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Card,
    Grid,
    Typography,
    useTheme,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { GET } from "../../Utilities/BaseService";
import { filterSelectionMessage } from "../../Utilities/constants";
import { MultiPermissionAuthorize } from "../../Utilities/MultiPermissionAuthorize";
import UpdatedSearchableInput from "../../Utilities/UpdatedSearchableInput";
import BaseTable from "../Table/BaseTable";
import SearchBar from "../Table/Components/SearchBar";
import { getSelectedFilters } from "./Property/Property";

type Filter = {
    search: string;
    district: number | "" | "all";
    village: number | "" | "all";
    hamlet: number | "" | "all";
    powerstation: number | "" | "all";
    property: number | "" | "all";
    id?: number;
    "connection type": number | "" | "all";
    selectedOrder: string;
    selectedColumn: string;
};

const initialFilter: Filter = {
    search: "",
    district: "",
    village: "",
    hamlet: "",
    powerstation: "",
    property: "",
    "connection type": "",
    selectedOrder: "",
    selectedColumn: "",
};

const initialClear = {
    district: false,
    village: false,
    hamlet: false,
    powerstation: false,
    search: false,
    surcharge: false,
    lineRent: false,
    unitChargeRule: false,
    connectionType: false,
};

const Customer = () => {
    const [rowsCount, setRowsCount] = useState(0);
    const [pagination, setPagination] = useState<any>({ page: 0, limit: 100 });
    const [clearAll, setClearAll] = useState(initialClear);
    const [filter, setFilter] = useState<any>(initialFilter);
    const [changeCount, setChangeCount] = useState(0);
    const [consumerId, setConsumerId] = useState<null | number>(null);
    const [feedback, setFeedback] = useState("");
    const theme = useTheme();
    // const mdDown = useMediaQuery(theme.breakpoints.down("md"));
    const [selectedFilters, setSelectedFilters] = useState<any>({});

    const location = useLocation();
    console.log(filter);

    const columnOptions = [
        { label: "ID", value: "id" },
        { label: "Name", value: "name" },
        { label: "Father Name", value: "fatherName" },
        { label: "CNIC", value: "cnic" },
        { label: "Mobile Number", value: "mobileNo" },
        { label: "None", value: "none" },
    ];

    const orderOptions = [
        { label: "Ascending", value: "ASC" },
        { label: "Descending", value: "DESC" },
        { label: "None", value: "none" },
    ];

    const getCustomer = () => {
        return GET("/customer/", {
            page: pagination.page + 1,
            limit: pagination.limit,
            ...Object.fromEntries(
                Object.entries(filter).filter(([k, v]) => v && v != "all")
            ),
            connectionType: filter["connection type"],
            "connection type": undefined,
        });
    };

    const { data, isLoading, isError, isSuccess, error } = useQuery(
        ["customer", pagination.page, pagination.limit, changeCount],
        getCustomer,
        {
            enabled: Boolean(changeCount),
            onSuccess(res) {
                if (res.data) {
                    setRowsCount(res.data.count);
                    const _selectedFills = getSelectedFilters(
                        res.data.rows[0],
                        ["district", "village", "hamlet", "powerstation"],
                        filter,
                        {
                            district: { label: "District", valueKey: "name" },
                            village: { label: "Village", valueKey: "name" },
                            hamlet: { label: "Hamlet", valueKey: "name" },
                            powerstation: {
                                label: "Power Station",
                                valueKey: "name",
                            },
                        }
                    );
                }
            },
        }
    );

    const not_filter_selected = useMemo(
        () => !filter.search && ["", "all"].includes(filter.district),
        [filter.search, filter.district]
    );

    /* ================= CLEAR ALL ================= */

    const handleClearAll = () => {
        // reset UI filters
        // reset applied filters
        setFilter(initialFilter);
        setSelectedFilters({});
        // notify child inputs
        setClearAll({
            district: true,
            village: true,
            hamlet: true,
            powerstation: true,
            search: true,
            surcharge: true,
            lineRent: true,
            unitChargeRule: true,
            connectionType: true,
        });

        // reset search trigger & pagination
        setChangeCount(0);
        setPagination({ page: 0, limit: 100 });

        // turn flags off after render
        setTimeout(
            () =>
                setClearAll({
                    district: false,
                    village: false,
                    hamlet: false,
                    powerstation: false,
                    search: false,
                    surcharge: false,
                    lineRent: false,
                    unitChargeRule: false,
                    connectionType: false,
                }),
            0
        );
    };

    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    // height: mdDown ? "auto" : `calc(100vh - 96px)`,
                }}
            >
                <Card
                    elevation={0}
                    sx={{
                        bgcolor: theme.palette.common.white,
                        p: 2,
                        mb: 2,
                    }}
                >
                    {/* Header */}
                    <Grid
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={2}
                    >
                        <Grid item>
                            <Typography variant="h5" color="text.primary">
                                Consumers
                            </Typography>
                        </Grid>

                        <Grid item>
                            <MultiPermissionAuthorize ops={["CREATE CUSTOMER"]}>
                                <Link
                                    to="/customer/consumers/add"
                                    style={{ textDecoration: "none" }}
                                >
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        startIcon={<Add />}
                                    >
                                        Add Consumer
                                    </Button>
                                </Link>
                            </MultiPermissionAuthorize>
                        </Grid>
                    </Grid>

                    {/* Description */}
                    <Box
                        sx={{
                            mt: 1.5,
                            mb: 3,
                            p: 1.5,
                            borderLeft: `4px solid ${theme.palette.secondary.main}`,
                            bgcolor: "grey.50",
                            borderRadius: 1,
                        }}
                    >
                        <Typography variant="body2" >
                           This section contains consumer profile information, including
                            Name, Father's Name, CNIC, contact details, and
                            address information. Connection records and
                            connection counts are not displayed here.
                        </Typography>

                        <Typography
                           variant="body2"
                           
                            sx={{
                                mt: 0.5,
                                direction: "rtl",
                                textAlign: "right",
                            }}
                        >
                            اس حصے میں صارف کی پروفائل معلومات شامل ہیں، جیسے
                            نام، والد کا نام، شناختی کارڈ نمبر، رابطہ معلومات
                            اور پتہ۔ یہاں کنکشن ریکارڈ یا کنکشن کی تعداد ظاہر
                            نہیں کی جاتی۔
                        </Typography>
                    </Box>

                    {/* Filters */}
                    <Grid container spacing={1.5}>
                        {/* your filter controls here */}

                        <Grid
                            item
                            container
                            xs={12}
                            md={12}
                            lg={12}
                            spacing={1.5}
                        >
                            <MultiPermissionAuthorize ops={["READ DISTRICT"]}>
                                <Grid item xs={12} md={4}>
                                    <UpdatedSearchableInput
                                        label="District"
                                        api="/region/district"
                                        filter={filter}
                                        setFilter={setFilter}
                                        clearAll={clearAll.district}
                                    />
                                </Grid>
                            </MultiPermissionAuthorize>

                            <MultiPermissionAuthorize
                                ops={["READ POWERSTATION"]}
                            >
                                <Grid item flex={1}>
                                    <UpdatedSearchableInput
                                        label="Powerstation"
                                        api="/powerstation"
                                        filter={filter}
                                        setFilter={setFilter}
                                        dep={filter.district}
                                        params={{
                                            district: filter.district,
                                        }}
                                        clearAll={clearAll.powerstation}
                                    />
                                </Grid>
                            </MultiPermissionAuthorize>

                            <MultiPermissionAuthorize ops={["READ VILLAGE"]}>
                                <Grid item xs={12} md={4}>
                                    <UpdatedSearchableInput
                                        api="/region/village"
                                        label="Village"
                                        filter={filter}
                                        setFilter={setFilter}
                                        dep={[
                                            filter.district,
                                            filter.powerstation,
                                        ]}
                                        params={{
                                            district: filter.district,
                                            powerstation: filter.powerstation,
                                        }}
                                        clearAll={clearAll.village}
                                    />
                                </Grid>
                            </MultiPermissionAuthorize>

                            <MultiPermissionAuthorize ops={["READ HAMLET"]}>
                                <Grid item xs={12} md={4}>
                                    <UpdatedSearchableInput
                                        key={filter.village}
                                        label="hamlet"
                                        api="/region/hamlet"
                                        filter={filter}
                                        setFilter={setFilter}
                                        dep={[
                                            filter.village,
                                            filter.powerstation,
                                        ]}
                                        params={{
                                            village: filter.village,
                                            powerstation: filter.powerstation,
                                        }}
                                        clearAll={clearAll.hamlet}
                                    />
                                </Grid>
                            </MultiPermissionAuthorize>

                            <Grid item xs={12} md={4}>
                                <SearchBar
                                    filter={filter}
                                    setFilter={setFilter}
                                    clear={clearAll.search}
                                />
                            </Grid>

                            <Grid item xs={12} md={1.4}>
                                <Button
                                    color="secondary"
                                    variant="outlined"
                                    onClick={() =>
                                        setChangeCount(changeCount + 1)
                                    }
                                    sx={{
                                        height: "100%",
                                    }}
                                    startIcon={<FilterAltOutlined />}
                                    fullWidth
                                >
                                    Search
                                </Button>
                            </Grid>

                            {/* CLEAR ALL */}
                            <Grid item xs={12} md={1.4}>
                                <Button
                                    color="error"
                                    variant="outlined"
                                    startIcon={<FilterAltOffOutlined />}
                                    onClick={handleClearAll}
                                    sx={{
                                        height: "100%",
                                        minWidth: theme.spacing(20),
                                    }}
                                    fullWidth
                                >
                                    Clear All
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </Card>

                <Grid item xs={12} md={4}></Grid>

                {(isError || not_filter_selected || !changeCount) && (
                    <Alert severity={isError ? "error" : "info"}>
                        {error
                            ? (error as any)?.response?.data?.message
                            : not_filter_selected || !changeCount
                              ? filterSelectionMessage
                              : "asdf"}
                    </Alert>
                )}

                <Box>
                    <BaseTable
                        headers={{
                            id: "ID",
                            name: {
                                label: "Name",
                                styles: (item: any) => ({
                                    maxWidth: "20ch",
                                    whiteSpace:
                                        item?.name?.length > 10
                                            ? "normal"
                                            : "nowrap",
                                }),
                                props: (item: any) => ({}),
                            },
                            fatherName: "Fathername",
                            cnic: "CNIC",
                            mobileNo: "Contact No",
                            district: "District",
                            village: "Village",
                            hamlet: "Hamlet",
                            powerstation: "Powerstation",
                        }}
                        data={data?.data?.rows.map((row: any) => ({
                            ...row,
                            district: row?.district?.name ?? "Global",
                            village: row?.village?.name ?? "Global",
                            hamlet: row?.hamlet?.name ?? "Global",
                            powerstation: row?.powerstation?.name ?? "Global",
                        }))}
                        delEndPoint="/customer"
                        feedback={feedback}
                        setFeedback={setFeedback}
                        rowsCount={rowsCount}
                        pagination={pagination}
                        setPagination={setPagination}
                        isLoading={isLoading}
                        load={Boolean(changeCount)}
                        permissions={{
                            edit: ["UPDATE CUSTOMER"],
                            delete: ["DELETE CUSTOMER"],
                        }}
                        queryKey={["customer"]}
                        reportName="Customers__List"
                        reportDetails={{
                            district: filter.district
                                ? filter.district
                                : undefined,
                            powerstation: filter.powerstation
                                ? filter.powerstation
                                : undefined,
                            village: filter.village
                                ? filter.village
                                : undefined,
                            hamlet: filter.hamlet ? filter.hamlet : undefined,
                        }}
                        defaultSelectedHeaders={[
                            "id",
                            "name",
                            "fatherName",
                            "cnic",
                            "mobileNo",
                        ]}
                    />
                </Box>
            </Box>
        </>
    );
};
export default Customer;
